/* eslint-disable jsx-a11y/no-onchange */
import { useState } from 'react';
import Button from 'components/button';
import MarkdownRender from 'components/markdown-render';
import MetaInfo from 'components/meta-info';
import QuizForm from 'components/quiz';
import Quiz from 'components-new/quiz';
import RateButton from 'components/rate-button';
import UnitsPicker from 'components/units-picker';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import styled from 'styled-components';
import { IS_PRODUCTION } from 'utils/constants';
import i18n, { SettingsType } from 'utils/i18n';
import { isUnitCompleted, useUserData, unitStatus } from 'utils/user';
import Modal from 'components/modal';
import Layout from './master-layout';
import Link from 'next/link';

import ProgressHexagon from 'components-new/progress-hexagon';

type UnitPageProps = {
  data: Unit | Quiz;
};

export const UnitPage: React.FC<UnitPageProps> = ({ data }) => {
  const { locale, asPath } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;
  const currentUnitIndex = data.collection.findIndex((item) => item.meta.id === data.meta.id);
  const { saveAction, achievements, dataLoaded, loggedIn, login } = useUserData();
  const [showModal, setShowModal] = useState<'Congratulations' | 'Login' | ''>('');
  const isModule = asPath.includes('module');
  const [randomHeroImage, setRandomHeroImage] = useState(null);
  const images = ['/images/way-to-go.png', '/images/well-done.png', '/images/you-did-it.png'];
  const imagesDE = ['/images/way-to-go-de.png', '/images/well-done-de.png', '/images/you-did-it-de.png'];

  useEffect(() => {
    setRandomHeroImage(Math.floor(Math.random() * 3));
  }, []);

  const unitCompleteAction: UserAchievement = {
    type: data.meta.type === 'Quiz' ? 'QuizComplete' : 'UnitComplete',
    contentId: data.meta.id,
    parentId: data.meta.parent.id,
    moduleId: data.meta.parent.id,
    learningPathId: data.learningPath.sys.id,
  };

  useEffect(() => {
    if (data.meta.type === 'Unit') {
      saveAction(unitCompleteAction);
    }
  }, [data.meta.id]); // eslint-disable-line

  // Keep checking if module is completed
  useEffect(() => {
    const moduleId = data.meta.parent.id;
    const moduleCompleted = !!achievements.find((ach) => ach.contentId === moduleId && ach.type === 'ModuleComplete');
    if (!moduleCompleted) {
      // Check if all units in module is completed
      const uncompletedUnits = data.collection.filter((unit) => !isUnitCompleted(achievements, unit));
      if (uncompletedUnits.length === 0) {
        // Module is complete
        // This should only fire when not logged in.
        const moduleCompleteAction: UserAchievement = {
          type: 'ModuleComplete',
          contentId: data.meta.parent.id,
        };
        saveAction(moduleCompleteAction);
      }
    }
  }, [JSON.stringify(achievements), data.meta.id]); // eslint-disable-line

  const parentItem = data.meta.breadcrumb[data.meta.breadcrumb.length - 2];
  const nextUnit = data.collection[currentUnitIndex + 1];

  const firstIncompleteSibling = data.collection.find((item) => !isUnitCompleted(achievements, item));
  const completed = isUnitCompleted(achievements, data);
  const moduleCompleted = !!achievements.find(
    (ach) => ach.type === 'ModuleComplete' && ach.contentId === data.meta.parent.id
  );

  return (
    <Layout breadcrumb={data.meta.breadcrumb} title={data.meta.title} description={data.meta.description}>
      <StyledUnitPage className="small-outer-padding unit-page">
        <UnitsPicker units={data.collection} parentTitle={parentItem?.label || ''} currentUnitId={data.meta.id} />
        <div className="paper">
          <ProgressHexagon status={unitStatus(achievements, data)} delay />
          {/* {!IS_PRODUCTION && <div>ID: {data.meta.id}</div>} */}
          <h1>{data.title}</h1>
          <MetaInfo readingTime={data.meta.readingTime} type="UNIT" />
          <div className="markdown-unit">
            <MarkdownRender markdown={data.body} />
          </div>
          {data.type === 'Quiz' && (
            <>
              {data.questionPools?.length > 0 && (
                <Quiz
                  nextUnit={nextUnit?.meta.path}
                  questionPools={data.questionPools}
                  onComplete={() => {
                    saveAction(unitCompleteAction);
                  }}
                />
              )}
              {data.quiz?.length > 0 && (
                <QuizForm
                  nextUnit={nextUnit.meta.path}
                  quiz={data.quiz}
                  onComplete={() => {
                    saveAction(unitCompleteAction);
                  }}
                />
              )}
            </>
          )}
          {/* If completed and not last unit  */}
          {nextUnit && completed && (
            <>
              <hr />
              <h2>
                {dictionary.nextUnit}: {nextUnit.title}
              </h2>
              <Button
                mode="primary"
                iconName="chevron-right"
                alignIcon="right"
                linkProps={{ href: nextUnit?.meta.path }}
              >
                {dictionary.continue}
              </Button>
            </>
          )}
          {/* If module complete - rate unit  */}
          {!nextUnit && moduleCompleted && completed && (
            <>
              <hr />
              <h2>{dictionary.moduleComplete}</h2>
              <div className="buttons">
                <Button
                  mode="primary"
                  iconName="chevron-right"
                  alignIcon="right"
                  onClick={() => (!loggedIn ? setShowModal('Login') : setShowModal('Congratulations'))}
                >
                  {dictionary.continue}
                </Button>
              </div>
            </>
          )}
          <Modal
            visible={showModal === 'Login'}
            headline={dictionary.bookmarkSignInToBookmark}
            onClose={() => setShowModal('')}
          >
            <div className="buttons">
              <Button mode="primary" size="small" onClick={() => login()}>
                {dictionary.login}
              </Button>
              <Button size="small" onClick={() => setShowModal('')}>
                {dictionary.cancel}
              </Button>
            </div>
          </Modal>
          <Modal
            visible={showModal === 'Congratulations'}
            onClose={() => setShowModal('')}
            headline={isModule || !nextUnit ? `${dictionary.keepUp}` : dictionary.nextModule}
            img={
              <div className="modal-image">
                <img
                  src={locale === 'de' ? imagesDE[randomHeroImage] : images[randomHeroImage]}
                  alt="Congratulations"
                />
              </div>
            }
          >
            <div>
              {isModule && (
                <>
                  <p>
                    {dictionary.modulePartOf}{' '}
                    <Link href={data?.learningPath?.slug}>
                      <span>{data?.fullLearningPath?.title}</span>
                    </Link>{' '}
                    <span>{dictionary.learningPath}.</span> <span>{dictionary.continueLearning}</span>
                  </p>
                  <div className="buttons">
                    <Button mode="primary" alignIcon="right" linkProps={{ href: `/browse` }}>
                      {dictionary.exploreOtherModules}
                    </Button>
                  </div>
                </>
              )}
              {!isModule && data.nextModule && (
                <>
                  <p>{data.nextModule.title}</p>
                  <div className="buttons">
                    <Button mode="primary" alignIcon="right" linkProps={{ href: `${data.nextModule.path}` }}>
                      {dictionary.nextModuleContinue}
                    </Button>
                  </div>
                  <Link href={data?.learningPath?.slug}>
                    <span>{dictionary.learningPathOverview}</span>
                  </Link>{' '}
                </>
              )}
              <div className="rate">
                <RateButton moduleId={data.meta.parent.id} />
              </div>
            </div>
          </Modal>
          {/* If last unit, but module incomplete. Go back to finish */}
          {dataLoaded && !nextUnit && !moduleCompleted && completed && firstIncompleteSibling && (
            <>
              <hr />
              <h2>{dictionary.moduleIncomplete}</h2>
              <Button
                mode="primary"
                iconName="chevron-right"
                alignIcon="right"
                linkProps={{ href: firstIncompleteSibling.meta.path }}
              >
                {dictionary.goBackToFinish}
              </Button>
            </>
          )}
        </div>
      </StyledUnitPage>
    </Layout>
  );
};

const StyledUnitPage = styled.article`
  .unit-selector {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 1rem 0;
  }

  .paper {
    position: relative;
    background-color: var(--body-background);
    border: 1px solid var(--border-white-high-contrast);
    box-shadow: 0 1.6px 3.6px 0 var(--box-shadow-medium), 0 0.3px 0.9px 0 var(--box-shadow-light);
    display: block;
    outline-color: var(--text);
    padding: 3rem;
    margin-bottom: 3rem;

    h1 {
      margin-right: 6rem;
    }

    .markdown-unit {
      a {
        color: #0078d4;
        text-decoration: underline;
      }
    }
  }

  .xp-hexagon {
    top: 3rem;
    right: 3rem;
  }

  .buttons {
    display: flex;
    gap: 0.5rem;
    margin: 1rem 0;
  }
`;

export default UnitPage;
